import { useState } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { Alert } from 'cpool-components/general/Alert';
import { AlertState } from 'cpool-components/general/Alert/types';
import { CollapsibleSection } from 'cpool-components/general/CollapsibleSection';
import { Button } from 'cpool-components/input/Button';
import { Spinner } from 'cpool-components/state/Spinner';
import { useTranslation } from 'react-i18next';
import { LuFiles, LuInbox, LuMail, LuSend } from 'react-icons/lu';

import { DemandModal } from './DemandModal';
import DocumentActions from './Header/DocumentActions';
import Categories from '~/features/leadDetails/components/Overview/DocumentCenter/Demands/Documents/Categories';
import OpenClaims from '~/features/leadDetails/components/Overview/DocumentCenter/OpenClaims';
import { DocumentsTable } from '~/features/leadDetails/components/Overview/DocumentCenter/Table/DocumentsTable';
import DemandedDocumentsContextProvider from '~/features/leadDetails/context/DemandedDocumentsContext';
import useLead from '~/features/leadDetails/hooks/useLead';
import useOpenClaims from '~/features/leadDetails/hooks/useOpenClaims';
import useRecordedDocuments from '~/features/leadDetails/hooks/useRecordedDocuments';

export const DocumentCenter = () => {
    const { t } = useTranslation();
    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();

    const [showDemandModal, setShowDemandModal] = useState(false);
    const [isDemandOpen, setIsDemandOpen] = useState(false);
    const [isOpenClaimsOpen, setIsOpenClaimsOpen] = useState(true);
    const [isSummaryOpen, setIsSummaryOpen] = useState(true);

    const { data: lead } = useLead(leadId);
    const { isError: isOpenClaimsError, openClaims } = useOpenClaims(leadId);
    const { documents, isError: isDocumentsError, isLoading: isLoadingDocuments } = useRecordedDocuments(leadId);

    const hasSecondApplicant = !!lead?.antragsteller2;

    const loadError = <Alert state={AlertState.ERROR}>{t('DOCUMENT_DEMANDER.LOAD_ERROR')}</Alert>;

    return (
        <div className="flex flex-col gap-3 pt-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h6 className="text-midnight">{t('LEAD_TABS.DOCUMENT_CENTER')}</h6>
                <DocumentActions leadId={leadId} />
            </div>

            {/* Catalogue stays inline; the send button opens DemandModal */}
            <DemandedDocumentsContextProvider>
                <CollapsibleSection
                    bgClassNames="bg-white rounded-md border-l border-blue-text py-2.5 pl-6 pr-2.5"
                    icon={<LuMail size={16} />}
                    panelClassNames="pr-3.5"
                    onOpenChange={setIsDemandOpen}
                    open={isDemandOpen}
                    title={t('DOCUMENT_DEMANDER.HEADER')}
                    titleClassNames="text-sm font-medium"
                >
                    <div className="flex flex-col gap-3 mt-3">
                        <Categories />
                        <div className="flex justify-end">
                            <Button
                                aria-label={t('DOCUMENT_DEMANDER.SEND_DEMAND_LABEL')}
                                iconLeft={<LuSend size={14} />}
                                onClick={() => setShowDemandModal(true)}
                            >
                                {t('DOCUMENT_DEMANDER.HEADER')}
                            </Button>
                        </div>
                    </div>
                </CollapsibleSection>
                <DemandModal leadId={leadId} open={showDemandModal} onClose={() => setShowDemandModal(false)} />
            </DemandedDocumentsContextProvider>

            {(isOpenClaimsError || openClaims.length > 0) && (
                <CollapsibleSection
                    bgClassNames="bg-white rounded-md border-l border-blue-text py-2.5 pl-6 pr-2.5"
                    icon={<LuFiles size={16} />}
                    panelClassNames="pr-3.5"
                    onOpenChange={setIsOpenClaimsOpen}
                    open={isOpenClaimsOpen}
                    title={t('DOCUMENT_DEMANDER.OPEN_CLAIMS.TITLE')}
                    titleClassNames="text-sm font-medium"
                >
                    <div className="mt-3">
                        {isOpenClaimsError ? (
                            loadError
                        ) : (
                            <OpenClaims
                                hasSecondApplicant={hasSecondApplicant}
                                leadId={leadId}
                                openClaims={openClaims}
                            />
                        )}
                    </div>
                </CollapsibleSection>
            )}

            <CollapsibleSection
                bgClassNames="bg-white rounded-md border-l border-blue-text py-2.5 pl-6 pr-2.5"
                icon={<LuInbox size={16} />}
                panelClassNames="pr-3.5"
                onOpenChange={setIsSummaryOpen}
                open={isSummaryOpen}
                title={t('DOCUMENT_DEMANDER.SUMMARY.TITLE')}
                titleClassNames="text-sm font-medium"
            >
                <div className="mt-3">
                    {isDocumentsError && loadError}
                    {!isDocumentsError && isLoadingDocuments && <Spinner />}
                    {!isDocumentsError && !isLoadingDocuments && (
                        <DocumentsTable documents={documents} hasSecondApplicant={hasSecondApplicant} leadId={leadId} />
                    )}
                </div>
            </CollapsibleSection>
        </div>
    );
};
